import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import {
  fetchPublicQuote,
  submitQuoteAction,
  formatMoney,
  type PublicQuoteView,
} from '@/hooks/useQuotes';
import { Button } from '@/components/ui/Button';

type PageState =
  | { phase: 'loading' }
  | { phase: 'loaded'; quote: PublicQuoteView }
  | { phase: 'gone' } // expired/not found
  | { phase: 'accepted' }
  | { phase: 'rejected' }
  | { phase: 'already_acted'; status: string }
  | { phase: 'error'; message: string };

export function AcceptQuote() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setState({ phase: 'gone' });
      return;
    }
    fetchPublicQuote(token)
      .then((quote) => {
        if (quote.status === 'accepted') {
          setState({ phase: 'already_acted', status: 'accepted' });
        } else if (quote.status === 'rejected') {
          setState({ phase: 'already_acted', status: 'rejected' });
        } else if (quote.status === 'withdrawn' || quote.status === 'expired') {
          setState({ phase: 'already_acted', status: quote.status });
        } else if (quote.expired) {
          setState({ phase: 'gone' });
        } else {
          setState({ phase: 'loaded', quote });
        }
      })
      .catch((err: Error & { code?: string }) => {
        if (err.code === 'gone' || err.message?.includes('expired')) {
          setState({ phase: 'gone' });
        } else {
          setState({ phase: 'error', message: err.message });
        }
      });
  }, [token]);

  async function handleAccept() {
    if (!token) return;
    setSubmitting(true);
    try {
      await submitQuoteAction(token, 'accept');
      setState({ phase: 'accepted' });
    } catch (err) {
      const e = err as Error & { code?: string };
      if (e.code === 'gone') setState({ phase: 'gone' });
      else setState({ phase: 'error', message: e.message });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleReject() {
    if (!token) return;
    setSubmitting(true);
    try {
      await submitQuoteAction(token, 'reject', rejectReason.trim() || undefined);
      setState({ phase: 'rejected' });
    } catch (err) {
      const e = err as Error & { code?: string };
      if (e.code === 'gone') setState({ phase: 'gone' });
      else setState({ phase: 'error', message: e.message });
    } finally {
      setSubmitting(false);
    }
  }

  if (state.phase === 'loading') {
    return (
      <FullPageCenter>
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </FullPageCenter>
    );
  }

  if (state.phase === 'gone') {
    return (
      <FullPageCenter>
        <div className="text-center">
          <Clock className="mx-auto h-12 w-12 text-amber-500" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            {t('quotes.expired')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('quotes.token_invalid')}
          </p>
        </div>
      </FullPageCenter>
    );
  }

  if (state.phase === 'accepted') {
    return (
      <FullPageCenter>
        <div className="text-center">
          <CheckCircle className="mx-auto h-12 w-12 text-emerald-500" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            {t('quotes.acceptance_thanks')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('quotes.acceptance_thanks_subtitle')}
          </p>
        </div>
      </FullPageCenter>
    );
  }

  if (state.phase === 'rejected') {
    return (
      <FullPageCenter>
        <div className="text-center">
          <XCircle className="mx-auto h-12 w-12 text-gray-400" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            {t('quotes.rejected_thanks')}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('quotes.rejected_thanks_subtitle')}
          </p>
        </div>
      </FullPageCenter>
    );
  }

  if (state.phase === 'already_acted') {
    const icon =
      state.status === 'accepted' ? (
        <CheckCircle className="mx-auto h-12 w-12 text-emerald-500" />
      ) : (
        <XCircle className="mx-auto h-12 w-12 text-gray-400" />
      );
    return (
      <FullPageCenter>
        <div className="text-center">
          {icon}
          <h1 className="mt-4 text-2xl font-bold text-gray-900 dark:text-white">
            {t(`quotes.status_${state.status}`)}
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('quotes.already_acted')}
          </p>
        </div>
      </FullPageCenter>
    );
  }

  if (state.phase === 'error') {
    return (
      <FullPageCenter>
        <div className="text-center">
          <AlertTriangle className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
            {t('quotes.error_title')}
          </h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{state.message}</p>
        </div>
      </FullPageCenter>
    );
  }

  // phase === 'loaded'
  const { quote } = state;
  const clinicAddress = quote.clinic_address as Record<string, string> | null;

  return (
    <div className="min-h-screen bg-gray-50 py-12 dark:bg-gray-900">
      <div className="mx-auto max-w-2xl px-4">
        {/* Clinic header */}
        <div className="mb-8 text-center">
          {quote.clinic_name && (
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              {quote.clinic_name}
            </p>
          )}
          {clinicAddress && (
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              {[clinicAddress.street, clinicAddress.city, clinicAddress.zip_code]
                .filter(Boolean)
                .join(', ')}
            </p>
          )}
        </div>

        {/* Quote card */}
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="mb-6 flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('quotes.public_accept_title')}
              </h1>
              {quote.quote_number && (
                <p className="mt-0.5 font-mono text-sm text-gray-500 dark:text-gray-400">
                  {quote.quote_number}
                </p>
              )}
            </div>
            <div className="text-right">
              {quote.valid_until && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {t('quotes.valid_until_label')}:{' '}
                  {new Date(quote.valid_until).toLocaleDateString('da-DK')}
                </p>
              )}
            </div>
          </div>

          {quote.notes && (
            <p className="mb-6 whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300">
              {quote.notes}
            </p>
          )}

          {/* Lines table */}
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="py-2.5 pl-4 pr-3 text-left text-xs font-semibold uppercase text-gray-500 dark:text-gray-300">
                    {t('quotes.line_field_description')}
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase text-gray-500 dark:text-gray-300">
                    {t('quotes.line_field_quantity')}
                  </th>
                  <th className="px-3 py-2.5 text-right text-xs font-semibold uppercase text-gray-500 dark:text-gray-300">
                    {t('quotes.line_field_unit_price')}
                  </th>
                  <th className="py-2.5 pl-3 pr-4 text-right text-xs font-semibold uppercase text-gray-500 dark:text-gray-300">
                    {t('quotes.totals_total')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                {quote.lines.map((line, i) => (
                  <tr key={i}>
                    <td className="py-3 pl-4 pr-3 text-sm text-gray-900 dark:text-white">
                      {line.description}
                    </td>
                    <td className="px-3 py-3 text-right text-sm text-gray-600 dark:text-gray-400">
                      {line.quantity} {line.unit}
                    </td>
                    <td className="px-3 py-3 text-right text-sm text-gray-600 dark:text-gray-400">
                      {formatMoney(line.unit_price_cents, quote.currency)}
                    </td>
                    <td className="py-3 pl-3 pr-4 text-right text-sm font-medium text-gray-900 dark:text-white">
                      {formatMoney(line.line_total_cents, quote.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="ml-auto mt-4 max-w-xs space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">
                {t('quotes.totals_subtotal')}
              </span>
              <span className="text-gray-900 dark:text-white">
                {formatMoney(quote.subtotal_cents, quote.currency)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">{t('quotes.totals_vat')}</span>
              <span className="text-gray-900 dark:text-white">
                {formatMoney(quote.vat_cents, quote.currency)}
              </span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold dark:border-gray-700">
              <span className="text-gray-900 dark:text-white">{t('quotes.totals_total')}</span>
              <span className="text-gray-900 dark:text-white">
                {formatMoney(quote.total_cents, quote.currency)}
              </span>
            </div>
          </div>

          {/* Subtitle */}
          <p className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            {t('quotes.public_accept_subtitle')}
          </p>

          {/* Action buttons */}
          {!rejecting ? (
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
              <Button onClick={handleAccept} disabled={submitting} className="w-full sm:w-auto">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                <span className="ml-1">{t('quotes.accept_button')}</span>
              </Button>
              <Button
                variant="secondary"
                onClick={() => setRejecting(true)}
                disabled={submitting}
                className="w-full sm:w-auto"
              >
                {t('quotes.reject_button')}
              </Button>
            </div>
          ) : (
            <div className="mt-6 space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('quotes.reject_reason_label')}
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  placeholder={t('quotes.reject_reason_placeholder')}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setRejecting(false)}
                  disabled={submitting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={submitting}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-500"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span className="ml-1">{t('quotes.reject_button')}</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FullPageCenter({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      {children}
    </div>
  );
}
